import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatNaira } from '@/lib/utils';
import { Sparkle, PaperPlaneTilt, ArrowRight, CircleNotch } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function QuickQuotePage() {
  const [desc, setDesc] = useState('');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getQuote = async () => {
    if (desc.trim().length < 10) { setError('Please describe the problem in more detail'); return; }
    setError(''); setLoading(true); setQuote(null);
    try {
      const { data } = await axios.post(`${API}/quick-quote`, { description: desc });
      setQuote({ ...data.quote, _powered_by: data.powered_by });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get quote');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8]" data-testid="quick-quote-page">
      <div className="max-w-[520px] mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <h1 className="font-heading text-2xl font-black text-[#1A4D2E]">Naija<span className="text-[#E86A33]">Fix</span></h1>
          </Link>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1A4D2E]/10 to-[#E86A33]/10 border border-[#1A4D2E]/20 rounded-full px-4 py-1.5 mb-4">
            <Sparkle size={16} weight="duotone" className="text-[#E86A33]" />
            <span className="text-xs font-bold text-[#1A4D2E]">AI-Powered Instant Quote</span>
          </div>
          <h2 className="font-heading text-3xl font-black text-[#1C1C1C] mb-2">Get a Price Estimate<br/>in Seconds</h2>
          <p className="text-[#5C5C5C] font-body text-sm">Describe your problem and our AI will analyze it — no account needed.</p>
        </div>

        {/* Input */}
        <div className="bg-white rounded-2xl border border-[#D4C5B3] p-5 shadow-sm mb-4">
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-3 block">Describe your problem</label>
          <textarea
            data-testid="quote-description"
            value={desc}
            onChange={e => setDesc(e.target.value)}
            placeholder="e.g. My kitchen sink pipe is leaking badly, water is pooling under the cabinet. The pipe looks corroded and I think it needs full replacement..."
            rows={5}
            className="w-full bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] border border-[#D4C5B3] rounded-xl px-4 py-3 text-[#1C1C1C] font-body text-sm leading-relaxed focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none resize-none placeholder:text-[#5C5C5C]/50"
          />
          <div className="flex items-center justify-between mt-2">
            <span className={`text-xs ${desc.length >= 10 ? 'text-[#2D6A4F]' : 'text-[#5C5C5C]'}`}>{desc.length} characters</span>
            <span className="text-xs text-[#5C5C5C]">Min 10 chars</span>
          </div>
          {error && <p className="text-red-600 text-sm mt-2 bg-red-50 px-3 py-2 rounded-lg" data-testid="quote-error">{error}</p>}
          <button
            data-testid="get-quote-button"
            onClick={getQuote}
            disabled={loading || desc.trim().length < 10}
            className="w-full mt-4 bg-[#1A4D2E] hover:bg-[#133922] text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 font-body"
          >
            {loading ? <><CircleNotch size={18} className="animate-spin" /> Analyzing...</> : <><Sparkle size={18} weight="fill" /> Get Instant Quote</>}
          </button>
        </div>

        {/* Result */}
        {quote && (
          <div className="bg-white rounded-2xl border border-[#D4C5B3] overflow-hidden shadow-sm animate-in" data-testid="quote-result">
            <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2D6A4F] px-5 py-4">
              <div className="flex items-center gap-2 mb-1">
                <Sparkle size={16} className="text-[#F4A261]" />
                <span className="text-white/70 text-xs font-bold uppercase tracking-wider">{quote._powered_by === 'ai' ? 'AI Estimate' : 'Smart Estimate'}</span>
              </div>
              <div className="font-heading text-2xl font-black text-white">{formatNaira(quote.total_min)} – {formatNaira(quote.total_max)}</div>
            </div>

            <div className="p-5">
              {quote.summary && <p className="text-sm text-[#1C1C1C] font-body mb-4 leading-relaxed">{quote.summary}</p>}

              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { label: 'Category', value: quote.category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) },
                  { label: 'Complexity', value: quote.complexity || 'Medium' },
                  { label: 'Est. Days', value: `${quote.estimated_days || 1} day${(quote.estimated_days || 1) > 1 ? 's' : ''}` },
                  { label: 'Sub Service', value: quote.sub_service || 'General' },
                ].map(item => (
                  <div key={item.label} className="bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] rounded-xl p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C]">{item.label}</div>
                    <div className="text-sm font-bold text-[#1C1C1C] font-body mt-0.5">{item.value}</div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 mb-4">
                {[['Labour', quote.labour_min, quote.labour_max], ['Materials', quote.materials_min, quote.materials_max]].map(([name, min, max]) => (
                  <div key={name} className="flex justify-between items-center py-2 border-b border-[#D4C5B3] text-sm font-body">
                    <span className="text-[#5C5C5C]">{name}</span>
                    <span className="font-bold text-[#E86A33]">{formatNaira(min)} – {formatNaira(max)}</span>
                  </div>
                ))}
              </div>

              {quote.recommendations && quote.recommendations.length > 0 && (
                <div className="bg-[#F4A261]/5 border border-[#F4A261]/20 rounded-xl p-4 mb-4">
                  <p className="text-xs font-bold text-[#E86A33] mb-2">Recommendations</p>
                  {quote.recommendations.map((tip, i) => (
                    <p key={i} className="text-xs text-[#5C5C5C] font-body mb-1">• {tip}</p>
                  ))}
                </div>
              )}

              <Link to="/register" className="block w-full bg-[#E86A33] hover:bg-[#D35A29] text-white font-bold py-3.5 rounded-xl text-center transition-all font-body" data-testid="signup-from-quote">
                Post This Job & Get Real Bids <ArrowRight size={16} weight="bold" className="inline ml-1" />
              </Link>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-[#E86A33] font-bold hover:underline">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
